import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CATEGORY_LABEL, RISK_CLASS, RISK_LABEL, fmtTimeAgo } from "@/lib/labels";

export default function Customer360() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/customers/${id}`).then((r) => setD(r.data)); }, [id]);
  if (!d) return <div className="p-8 text-[13px]" style={{ color: "var(--text-muted)" }}>Loading customer…</div>;
  const c = d.customer;
  return (
    <div className="px-4 py-5 md:px-8 md:py-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--text-muted)" }}>Customer 360</div>
          <h1 className="font-display text-[26px]" data-testid="customer-name">{c.name}</h1>
          <div className="font-mono text-[12px] mt-1" style={{ color: "var(--text-muted)" }}>{c.email || c.phone}</div>
        </div>
        <div className="card p-4 text-center min-w-[160px]" data-testid="risk-score">
          <div className="font-mono text-[26px]">{d.risk_score}</div>
          <div className="text-[10.5px] uppercase tracking-wider mt-1" style={{ color: "var(--text-muted)" }}>Customer risk</div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 mb-5">
        <KvCard k="Plan" v={c.plan || "Unknown"} bad={!c.plan} />
        <KvCard k="Annual value" v={c.arr ? `${c.currency || "USD"} ${Number(c.arr).toLocaleString()}` : "—"} />
        <KvCard k="Entitlement" v={c.entitlement_active ? "Active" : "Inactive"} bad={!c.entitlement_active} />
        <KvCard k="Billing platform" v={c.billing_platform || "—"} />
        <KvCard k="Subscription state" v={c.subscription_state || "—"} />
        <KvCard k="Price" v={c.price_amount ? `${c.currency || "USD"} ${c.price_amount}` : "—"} />
      </div>

      {/* Where these facts came from. Without this, a customer nobody has a
          record of looks identical to one we never looked up — and the second
          is a gap in configuration that someone can actually fix. */}
      <div className="text-[11.5px] mb-5" data-testid="profile-provenance"
           style={{ color: "var(--text-muted)" }}>
        {c.profile_synced_at ? (
          <>Profile from <span className="font-medium">{c.profile_source}</span>,
             synced {fmtTimeAgo(c.profile_synced_at)}.
             {!c.plan && " No plan on record there."}</>
        ) : (
          <>No profile lookup configured, so plan and value are unknown and the
             attention score is deliberately conservative.</>
        )}
      </div>

      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-7 card p-5" data-testid="customer-cases">
          <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>Previous cases</div>
          {d.cases.length === 0 && <div className="text-[12px]" style={{ color: "var(--text-muted)" }}>No prior cases.</div>}
          <div className="space-y-1.5">
            {d.cases.map((cs) => (
              <Link key={cs.id} to={`/cases/${cs.id}`} className="block px-3 py-2.5 rounded-md hover-wash-subtle" data-testid={`customer-case-${cs.case_ref}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-[11px]" style={{ color: "var(--text-muted)" }}>{cs.case_ref}</span>
                  <span className={`badge ${RISK_CLASS[cs.risk_type]}`}>{RISK_LABEL[cs.risk_type]}</span>
                </div>
                <div className="text-[12.5px] leading-snug">{cs.title}</div>
                <div className="mt-1 text-[11px]" style={{ color: "var(--text-muted)" }}>
                  {CATEGORY_LABEL[cs.category] || cs.category} · {cs.status} · {fmtTimeAgo(cs.created_at)}
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 card p-5" data-testid="customer-signals">
          <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>Sentiment history</div>
          <div className="space-y-1">
            {d.sentiment_history.map((s, i) => (
              <div key={i} className="flex items-center justify-between text-[12px] py-1.5 border-b" style={{ borderColor: "var(--border)" }}>
                <span style={{ color: "var(--text-secondary)" }} className="font-mono text-[11px]">{fmtTimeAgo(s.at)}</span>
                <span className={`badge ${s.sentiment === "angry" ? "b-rev" : s.sentiment === "frustrated" ? "b-sla" : "b-muted"}`}>{s.sentiment || "neutral"}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const KvCard = ({ k, v, bad, mono }) => (
  <div className="card p-4 col-span-6 md:col-span-4 lg:col-span-2">
    <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>{k}</div>
    <div className={`${mono ? "font-mono text-[12.5px]" : "text-[13.5px] font-medium"} ${bad ? "text-rev" : ""} truncate`} title={v}>{v}</div>
  </div>
);
